import React, { useMemo } from 'react';
import { Box, DateInput, dateValidatorByFormat, Hint, InfoOutlineIcon, Inscription, ListItem, PlanStatuses, Position, Text } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { DEFAULT_DATE_FORMAT, REQUIRED_DATE_TEXT } from '@/common/constants';
import { ListItemType } from '@/common/types';
import { useGetOrganizationPlansQuery } from '@/modules/employee/Employee/queries/useGetOrganizationPlans.query';
import { CancelationHsaRules } from '@/modules/plan/PlanTermination/PlanTerminationModalForms/hooks/usePlanTerminationHsaTransferFields';

import { useGetEmployerPendingTerminationPlansQuery } from '../queries/useGetEmployerPendingTerminationPlans.query';
import { EmployerTerminationPlan, useGetEmployerTerminationPlansListQuery } from '../queries/useGetEmployerTerminationPlansList.query';

const HSA_ACCOUNT_TYPE = 'HSA';

const PLAN_STATUSES = [
  PlanStatuses.ACTIVE,
  PlanStatuses.PENDING_TERMINATION,
  PlanStatuses.TERMINATION_IN_PROGRESS,
  PlanStatuses.TERMINATED,
];

const StyledInfoIcon = styled(InfoOutlineIcon)`
  fill: ${({ theme }) => theme.colors.iconDisabled};
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    opacity: 1 ;
    fill: ${({ theme }) => theme.colors.iconAccent};
  }
`;

const dateValidator = (dateString?: string, showError?: boolean) => {
  if (!dateString && showError) return REQUIRED_DATE_TEXT;
  if (!dateValidatorByFormat(dateString, DEFAULT_DATE_FORMAT) && showError) return 'Invalid date format';
  return undefined;
};

interface Props {
  orgId?: string,
  terminationDate?: string,
  viewMode?: boolean,
  isSubmitted?: boolean,
  statuses?: string[],
  endOfCoverage?: (planId: string) => string;
  lastDayToSubmitClaim?: (planId: string) => string;
  isEndOfCoverageError?: (planId: string) => boolean,
  isClaimLastDateError?: (planId: string) => boolean,
  setEndOfCoverage?: (planId: string, date: string) => void,
  setLastDayToSubmitClaim?: (planId: string, date: string) => void,
  isTerminatedOrg?: boolean
}

export const useEmployerTerminationPlanList = ({
  orgId,
  terminationDate,
  viewMode,
  endOfCoverage,
  lastDayToSubmitClaim,
  isSubmitted,
  isEndOfCoverageError,
  isClaimLastDateError,
  setEndOfCoverage,
  setLastDayToSubmitClaim,
  statuses,
  isTerminatedOrg,
}: Props) => {
  const {
    formattedData,
    isLoading,
    isHsaAccountExist,
    hsaStartDate,
    hsaPlanIds,
  } = useGetEmployerTerminationPlansListQuery(orgId, terminationDate, statuses);

  const {
    formattedData: pendingFormattedData,
    isLoading: isPendingLoading,
  } = useGetEmployerPendingTerminationPlansQuery(orgId);

  const {
    formattedData: plans,
    isLoading: isPlansLoading
  } = useGetOrganizationPlansQuery(isTerminatedOrg ? orgId : undefined, PLAN_STATUSES);

  const isHsaPlanExist = useMemo(() => {
    return isTerminatedOrg && plans?.some((plan) => plan.accountType === HSA_ACCOUNT_TYPE) || false;
  }, [plans, isTerminatedOrg]);

  const terminatedOrgPlans: EmployerTerminationPlan[] = useMemo(() => {
    if (!plans) return [];
    return plans.map((plan) => ({
        id: plan.id,
        name: plan.name,
        startDate: plan?.planYear?.validFrom,
        endDate: plan?.planYear?.validTo,
        claimLastDate: plan?.claimLastDate || terminationDate,
        coverageEndDate: plan?.coverageEndDate || terminationDate,
    }));
  }, [plans, terminationDate]);

  const isEmptyPlansList = useMemo(() => {
    return !formattedData || formattedData.length === 0 || (isTerminatedOrg && terminatedOrgPlans.length === 0);
  }, [formattedData, isTerminatedOrg, terminatedOrgPlans]);

  const currentPlans = useMemo(() => {
    if (isTerminatedOrg) {
      return terminatedOrgPlans;
    }
    return formattedData;
  }, [isTerminatedOrg, terminatedOrgPlans, formattedData]);

  const currentCoverageData = useMemo(() => {
    if (isEmptyPlansList) return [];
    const mergedData = currentPlans.map((item: EmployerTerminationPlan) => {
      const pendingItem = pendingFormattedData.find((pending) => pending.planId === item.id);
      return {
        ...item,
        coverageEndDate: pendingItem?.coverageEndDate || item.coverageEndDate,
        claimLastDate: pendingItem?.claimLastDate || item.claimLastDate,
        ...!item.coverageEndDate ? { endOfCoverageDisabled: true } : {},
      };
    });
    return mergedData;
  }, [currentPlans, pendingFormattedData, isEmptyPlansList]);

  const currentValidFromDates = useMemo(() => {
    return currentCoverageData.reduce((acc: Record<string, string>, item: EmployerTerminationPlan) => {
      if (item.startDate) {
        acc[item.id] = item.startDate;
      }
      return acc;
    }, {});
  }, [currentCoverageData]);

  const hsaTransferOfAssets = useMemo(() => {
    if (isHsaAccountExist && hsaPlanIds.length > 0 && pendingFormattedData.length > 0) {
      const isBulkTransfer = pendingFormattedData.some((item) => item.hsaTransferOfAssets === CancelationHsaRules.BULK_TRANSFER);
      return isBulkTransfer ? CancelationHsaRules.BULK_TRANSFER : CancelationHsaRules.EMPLOYEE_OWNED;
    }
    return undefined;
  }, [isHsaAccountExist, hsaPlanIds, pendingFormattedData]);

  const hsaTransferOfAssetsView = useMemo(() => {
    if (isHsaPlanExist && pendingFormattedData.length > 0) {
      const isBulkTransfer = pendingFormattedData.some((item) => item.hsaTransferOfAssets === CancelationHsaRules.BULK_TRANSFER);
      return isBulkTransfer ? CancelationHsaRules.BULK_TRANSFER : CancelationHsaRules.EMPLOYEE_OWNED;
    }
    return undefined;
  }, [isHsaPlanExist, pendingFormattedData]);

  const formattedFields = useMemo(() => {
    const formatted = currentPlans.map((item: EmployerTerminationPlan) => ({
      id: `${item.id}`,
      fields: [
        {
          key: 'planName',
          type: ListItemType.Node,
          hasTip: true,
          value: item.name,
          title: item.name,
          flex: 1.8,
          node: (
            <Text
              title={item.name}
              size="medium"
              weight="bold"
              color="textBody"
              margin={{ right: 'spacing16' }}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {item.name}
            </Text>
          ),
        },

        {
          key: 'planStartDate',
          type: ListItemType.Text,
          value: item.startDate,
          title: item.startDate,
          fieldStyle: { alignItems: 'flex-start', flex: '0.8' },
        },
        {
          key: 'planEndDate',
          type: ListItemType.Text,
          value: item.endDate,
          title: item.endDate || '-',
          fieldStyle: { alignItems: 'flex-start', flex: '1' },
        }
      ],
    }));
    return formatted;
  }, [
    currentPlans,
  ]);

  const formattedCoverageFields = useMemo(() => {
    const formatted = currentCoverageData.map((item: EmployerTerminationPlan) => ({
      id: `${item.id}`,
      fields: [
        {
          key: 'planName',
          type: ListItemType.Node,
          hasTip: true,
          value: item?.name || '',
          title: item?.name || '',
          flex: 1.7,
          node: (
            <Text
              title={item?.name || ''}
              size="medium"
              weight="bold"
              color="textBody"
              margin={{ right: 'spacing16' }}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {item?.name || ''}
            </Text>
          ),
        },
        {
          key: 'endOfCoverage',
          type: viewMode ? ListItemType.Text : ListItemType.Node,
          value: item?.coverageEndDate || '-',
          title: item?.coverageEndDate || '-',
          fieldStyle: { alignItems: 'flex-start', flex: '1.1' },
          node: (!item?.coverageEndDate || item?.endOfCoverageDisabled) ? 
          (
            <Box
              direction="row"
              align="center"
              width={{ max: '150px'}}
              height={{ max: '40px', min: '40px' }}
              gap="spacing8"
            >
              <Inscription
                color="textBody"
                lineHeight="20px"
              >
                {endOfCoverage && endOfCoverage(item.id) || '-'}
              </Inscription>
              <Hint
                hintAlign="vertical"
                hintWidth={230}
                hintElement={(
                  <StyledInfoIcon />
                )}
              >
                <Inscription textAlign='center'>
                  This plan pays claims outside coverage period so the coverage will end on the runout when terminated.
                </Inscription>
              </Hint>
            </Box>
          )
          : (
            <Box width={{ max: '150px'}}>
              <DateInput
                id='EmployerTerminationCoverageDatesForm_endOfCoverage'
                testId="EmployerTerminationCoverageDatesForm_endOfCoverage"
                className="period-picker"
                placeholder='Select date'
                isPicker
                errorText={endOfCoverage && dateValidator(endOfCoverage(item.id), isSubmitted)}
                onChange={setEndOfCoverage ? (date: string) => setEndOfCoverage(item.id, date) : undefined}
                value={endOfCoverage && endOfCoverage(item.id) || ''}
                pickerSide={Position.BOTTOM}
                isError={isEndOfCoverageError && isEndOfCoverageError(item.id)}
                minDate={item.startDate ? dayjs(item.startDate) : undefined}
              />  
            </Box>
          ),
        },
        {
          key: 'lastDayToSubmitClaim',
          type: viewMode? ListItemType.Text : ListItemType.Node,
          value: item.claimLastDate || '-',
          title: item.claimLastDate || '-',
          fieldStyle: { alignItems: 'flex-start', flex: '1' },
          node: (
            <Box width={{ max: '150px'}}>
              <DateInput
                id='EmployerTerminationCoverageDatesForm_lastDayToSubmitClaim'
                testId="EmployerTerminationCoverageDatesForm_lastDayToSubmitClaim"
                className="period-picker"
                placeholder='Select date'
                isPicker
                errorText={lastDayToSubmitClaim && dateValidator(lastDayToSubmitClaim(item.id), isSubmitted)}
                onChange={(date: string) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                  setLastDayToSubmitClaim && setLastDayToSubmitClaim(item.id, date);
                  if ((!item?.coverageEndDate || item?.endOfCoverageDisabled) && setEndOfCoverage) {
                    setEndOfCoverage(item.id, date);
                  }
                }}
                value={lastDayToSubmitClaim && lastDayToSubmitClaim(item.id) || ''}
                pickerSide={Position.BOTTOM}
                isError={isClaimLastDateError && isClaimLastDateError(item.id)}
                minDate={item.startDate ? dayjs(item.startDate) : undefined}
              />  
            </Box>
          ),
        }
      ],
    }));
    return formatted;
  }, [
    currentCoverageData,
    endOfCoverage,
    lastDayToSubmitClaim,
    isSubmitted,
    isEndOfCoverageError,
    isClaimLastDateError,
    setEndOfCoverage,
    setLastDayToSubmitClaim,
    viewMode,
  ]);

  return {
    data: formattedFields as ListItem[],
    coverageData: formattedCoverageFields as ListItem[],
    isLoading: isLoading || isPendingLoading || isPlansLoading,
    isEmptyPlansList,
    isHsaAccountExist: isHsaAccountExist || isHsaPlanExist,
    hsaStartDate,
    currentCoverageData,
    currentValidFromDates,
    hsaPlanIds,
    hsaTransferOfAssets: hsaTransferOfAssetsView || hsaTransferOfAssets,
  };
};
